import React, {useEffect, useState} from "react";
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from "yup";
import {
    Alert,
    Button,
    Col,
    Dropdown,
    FormControl,
    FormGroup,
    FormLabel,
    InputGroup,
    Modal,
    ModalTitle,
    Row,
    Spinner,
} from "react-bootstrap";
import {BsChevronDown, BsEnvelope, BsPlusLg, BsTrash} from "react-icons/bs";
import '../../components/StyledSelect.css';

const CareUnitMultiSelect = ({selectedIds, careUnits, onChange}) => {
    // Sort care units: selected first, then alphabetically within each group
    const sortedCareUnits = [...(careUnits || [])].sort((a, b) => {
        const aSelected = selectedIds.includes(a.organizationId);
        const bSelected = selectedIds.includes(b.organizationId);

        // If one is selected and the other isn't, selected goes first
        if (aSelected && !bSelected) return -1; // a first
        if (!aSelected && bSelected) return 1;  // b first

        // Both selected or both unselected: sort alphabetically
        return a.name.localeCompare(b.name);
    });

    // Convert selected IDs to full care unit objects for display
    const selectedCareUnits = selectedIds
        .map(id => (careUnits || []).find(cu => cu.organizationId === id))
        .filter(Boolean);

    // Add or remove a care unit ID from the selection, this controls which ones have their checkbox checked
    const toggleCareUnit = (clickedId) => {
        const isAlreadySelected = selectedIds.includes(clickedId);

        if (isAlreadySelected) {
            // Remove: keep every ID except the one that was clicked
            const withoutClicked = selectedIds.filter(id => id !== clickedId);
            onChange(withoutClicked);
        } else {
            // Add: copy existing IDs and append the clicked one
            const withClicked = [...selectedIds, clickedId];
            onChange(withClicked);
        }
    };

    const hasSelection = selectedCareUnits.length > 0;

    const getSummaryText = () => {
        if (!hasSelection) return 'Välj vårdenheter...';
        if (selectedCareUnits.length === 1) return selectedCareUnits[0].name;
        return `${selectedCareUnits.length} vårdenheter valda`;
    };

    return (
        <Dropdown autoClose="outside">
            <Dropdown.Toggle as="div" bsPrefix="multiselect" className="multiselect-toggle">
                <span className={hasSelection ? 'multiselect-summary' : 'multiselect-placeholder'}>
                    {getSummaryText()}
                </span>
                <BsChevronDown className="multiselect-chevron"/>
            </Dropdown.Toggle>
            <Dropdown.Menu className="multiselect-menu">
                {sortedCareUnits.map(cu => {
                    const isChecked = selectedIds.includes(cu.organizationId);
                    return (
                        <Dropdown.Item
                            key={cu.organizationId}
                            className={isChecked ? 'multiselect-item-checked' : ''}
                            onClick={(e) => { e.preventDefault(); toggleCareUnit(cu.organizationId); }}
                        >
                            <input type="checkbox" className="multiselect-checkbox" checked={isChecked} readOnly/>
                            {cu.name}
                        </Dropdown.Item>
                    );
                })}
            </Dropdown.Menu>
        </Dropdown>
    );
};

const SaveOpticianDialog = ({
                                saveErrorMessage,
                                onSaveClicked,
                                onUpdateClicked,
                                onCancelClicked,
                                onDeleteClicked,
                                careUnits,
                                opticianForm,
                                loggedInUserRole,
                                isLoading,
                            }) => {
    const isUpdating = !!opticianForm.name;

    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        if (saveErrorMessage) {
            setConfirmAction(null);
        }
    }, [saveErrorMessage]);

    const isGlobalAdmin = loggedInUserRole === 'ADMIN';
    const isCareUnitAdmin = loggedInUserRole === 'CARE_UNIT_ADMIN';
    const isCareUnitRole = isCareUnitAdmin || loggedInUserRole === 'CARE_UNIT';
    const canDelete = isGlobalAdmin || isCareUnitAdmin;

    const validationSchema = Yup.object().shape({
        organizationEmail: Yup.string().email("Ogiltig e-post").max(254, "Max 254 tecken").required("Obligatoriskt fält"),
        adminEmail: isUpdating ? Yup.string() : Yup.string().email("Ogiltig e-post").max(254, "Max 254 tecken").required("Obligatoriskt fält"),
        adminEmailRepetition: isUpdating ? Yup.string() : Yup.string().oneOf([Yup.ref("adminEmail")], "E-postadresserna matchar inte").required("Obligatoriskt fält"),
        adminUsername: isUpdating ? Yup.string() : Yup.string().required("Admin-användarnamn är obligatoriskt"),
        name: Yup.string().max(150, "Max 150 tecken").required("Obligatoriskt fält"),
        hsaId: Yup.string().required("Obligatoriskt fält"),
        careUnitOrganizationIds: isCareUnitRole ? Yup.array() : Yup.array().min(1, "Välj minst en vårdenhet"),
        streetAddress: Yup.string().max(150, "Max 150 tecken").required("Obligatoriskt fält"),
        zipCode: Yup.string()
            .transform(value => value?.replace(/\s/g, ''))
            .matches(/^\d{5}$/, "Postnummer måste vara 5 siffror")
            .required("Obligatoriskt fält"),
        city: Yup.string().max(100, "Max 100 tecken").required("Obligatoriskt fält"),
        contactName: Yup.string().max(100, "Max 100 tecken").required("Obligatoriskt fält"),
        contactPhoneNumber: Yup.string()
            .transform(value => value?.replace(/[\s\-()]/g, ''))
            .matches(/^(?:\+46|0)\d{6,9}$/, "Ogiltigt svenskt telefonnummer")
            .required("Obligatoriskt fält"),
    });

    return (
        <Modal show={true} onHide={onCancelClicked} backdrop="static" keyboard={true} size="lg">
            <div className="SaveOpticianDialog">
                <Modal.Header closeButton>
                    <ModalTitle>
                        <h3 className="mb-0">{isUpdating ? "Uppdatera optiker" : "Skapa optiker"}</h3>
                    </ModalTitle>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                        initialValues={{
                            organizationEmail: opticianForm.organizationEmail || "",
                            adminEmail: opticianForm.adminEmail || "",
                            adminEmailRepetition: opticianForm.adminEmailRepetition || "",
                            adminUsername: opticianForm.adminUsername || "",
                            name: opticianForm.name || "",
                            hsaId: opticianForm.hsaId || "",
                            careUnitOrganizationIds: opticianForm.careUnitOrganizationIds || [],
                            streetAddress: opticianForm.streetAddress || "",
                            zipCode: opticianForm.zipCode || "",
                            city: opticianForm.city || "",
                            contactName: opticianForm.contactName || "",
                            contactPhoneNumber: opticianForm.contactPhoneNumber || "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values) => {
                            if (onUpdateClicked) {
                                const {adminEmail, adminEmailRepetition, ...updateData} = values;
                                onUpdateClicked({
                                    ...updateData,
                                    organizationId: opticianForm.organizationId
                                });
                            } else {
                                onSaveClicked(values);
                            }
                        }}
                    >
                        {({isValid, touched, errors, values}) => (
                            <Form className="SaveOpticianForm">
                                <fieldset disabled={isLoading}>
                                    <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                        <FormLabel column sm={4}>Optikerns namn</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="name"
                                                   placeholder="Ex: Optikus prime"/>
                                            <ErrorMessage name="name" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                        <FormLabel column sm={4}>HSA-id</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="hsaId"
                                                   placeholder="Ex: SE12345678901234"/>
                                            <ErrorMessage name="hsaId" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    {!isCareUnitRole && (
                                    <FormGroup as={Row} className="SaveOpticianDialog-row">
                                        <FormLabel column sm={4}>Vårdenhet(er)</FormLabel>
                                        <Col sm={8}>
                                            <Field name="careUnitOrganizationIds">
                                                {({field, form}) => (
                                                    <CareUnitMultiSelect
                                                        selectedIds={field.value}
                                                        careUnits={careUnits}
                                                        onChange={(ids) => form.setFieldValue('careUnitOrganizationIds', ids)}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="careUnitOrganizationIds" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>
                                    )}

                                    <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                        <FormLabel column sm={4}>Gatuadress</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="streetAddress"
                                                   placeholder="Ex: Storgatan 15B"/>
                                            <ErrorMessage name="streetAddress" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                        <FormLabel column sm={4}>Postnummer / Stad</FormLabel>
                                        <Col sm={4}>
                                            <Field as={FormControl} type="text" name="zipCode"
                                                   placeholder="Ex: 123 45"/>
                                            <ErrorMessage name="zipCode" component="div" className="text-danger small"/>
                                        </Col>
                                        <Col sm={4}>
                                            <Field as={FormControl} type="text" name="city"
                                                   placeholder="Ex: Stockholm"/>
                                            <ErrorMessage name="city" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                        <FormLabel column sm={4}>Organisations-email</FormLabel>
                                        <Col sm={8}>
                                            <InputGroup>
                                                <InputGroup.Text className="SaveOpticianDialog-input-icon"><BsEnvelope/></InputGroup.Text>
                                                <Field as={FormControl} type="email" name="organizationEmail"
                                                       placeholder="Ex: info@optiker.se"/>
                                            </InputGroup>
                                            <ErrorMessage name="organizationEmail" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                        <FormLabel column sm={4}>Kontaktperson</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="contactName"
                                                   placeholder="För- och efternamn"/>
                                            <ErrorMessage name="contactName" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                        <FormLabel column sm={4}>Telefonnummer</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="contactPhoneNumber"
                                                   placeholder="Ex: 0701234567"/>
                                            <ErrorMessage name="contactPhoneNumber" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    {!isUpdating && (
                                        <>
                                            <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                                <FormLabel column sm={4}>Admin-email</FormLabel>
                                                <Col sm={8}>
                                                    <InputGroup>
                                                        <InputGroup.Text
                                                            className="SaveOpticianDialog-input-icon"><BsEnvelope/></InputGroup.Text>
                                                        <Field as={FormControl} type="email" name="adminEmail"
                                                               placeholder="Email för admin-användare"/>
                                                    </InputGroup>
                                                    <ErrorMessage name="adminEmail" component="div"
                                                                  className="text-danger small"/>
                                                </Col>
                                            </FormGroup>

                                            <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                                <FormLabel column sm={4}>Repetera admin-email</FormLabel>
                                                <Col sm={8}>
                                                    <InputGroup>
                                                        <InputGroup.Text
                                                            className="SaveOpticianDialog-input-icon"><BsEnvelope/></InputGroup.Text>
                                                        <Field as={FormControl} type="email" name="adminEmailRepetition"
                                                               placeholder="Repetera email"/>
                                                    </InputGroup>
                                                    <ErrorMessage name="adminEmailRepetition" component="div"
                                                                  className="text-danger small"/>
                                                </Col>
                                            </FormGroup>

                                            <FormGroup as={Row} className="SaveOpticianDialog-row align-items-center">
                                                <FormLabel column sm={4}>Admin-användarnamn</FormLabel>
                                                <Col sm={8}>
                                                    <Field as={FormControl} type="text" name="adminUsername"
                                                           placeholder="Användarnamn för admin-användare"/>
                                                    <ErrorMessage name="adminUsername" component="div"
                                                                  className="text-danger small"/>
                                                </Col>
                                            </FormGroup>
                                        </>
                                    )}
                                </fieldset>

                                {saveErrorMessage &&
                                    <Alert variant="danger" className="mt-2">{saveErrorMessage}</Alert>}

                                {/* Footer */}
                                <div className="SaveOpticianDialog-footer">
                                    {confirmAction === 'delete' && (
                                        <>
                                            <div className="SaveOpticianDialog-footer-message">
                                                {isCareUnitAdmin
                                                    ? <>Vill du koppla bort optikern <strong>{opticianForm.name}</strong> från din vårdenhet?</>
                                                    : <>Vill du ta bort optikern <strong>{opticianForm.name}</strong>? Detta kan inte ångras.</>
                                                }
                                            </div>
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button variant="danger" disabled={isLoading}
                                                        onClick={() => setConfirmAction(null)}>
                                                    Avbryt
                                                </Button>
                                                <Button variant="primary" disabled={isLoading}
                                                        onClick={() => onDeleteClicked({organizationId: opticianForm.organizationId})}>
                                                    {isLoading
                                                        ? <><Spinner animation="border" size="sm"/> {isCareUnitAdmin ? 'Kopplar bort...' : 'Tar bort...'}</>
                                                        : <><BsTrash style={{marginRight: '6px'}}/>{isCareUnitAdmin ? 'Koppla bort' : 'Ta bort optiker'}</>
                                                    }
                                                </Button>
                                            </div>
                                        </>
                                    )}

                                    {!confirmAction && (
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex gap-3">
                                                {isUpdating && canDelete && onDeleteClicked && (
                                                    <Button
                                                        variant="link"
                                                        className="SaveOpticianDialog-link-btn text-danger"
                                                        onClick={() => setConfirmAction('delete')}
                                                        disabled={isLoading}
                                                    >
                                                        <BsTrash style={{marginRight: '5px'}}/>{isCareUnitAdmin ? 'Koppla bort' : 'Ta bort'}
                                                    </Button>
                                                )}
                                            </div>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="danger"
                                                    onClick={onCancelClicked}
                                                    disabled={isLoading}
                                                >
                                                    Avbryt
                                                </Button>
                                                {onUpdateClicked ? (
                                                    <Button type="submit" variant="primary" disabled={isLoading}>
                                                        {isLoading ? <><Spinner animation="border"
                                                                                size="sm"/> Uppdaterar...</> : "Uppdatera"}
                                                    </Button>
                                                ) : (
                                                    <Button type="submit" variant="primary" disabled={isLoading}>
                                                        {isLoading ? <><Spinner animation="border"
                                                                                size="sm"/> Skapar...</> : <><BsPlusLg
                                                            style={{marginRight: '6px'}}/>Skapa</>}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </div>
        </Modal>
    );
};
export default SaveOpticianDialog;
